import { slotStartIso } from '../data/booking';
import { api } from './client';

/**
 * Which slots are already full, for one day at one address.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 * The app had no capacity concept at all. `timeSlots` in `data/booking.ts` is
 * a GENERATED grid - 7:00 AM to 8:00 PM every thirty minutes - and the only
 * thing that ever greyed a tile was the clock: the two-hour booking notice, or
 * a reschedule's stricter cutoff. Nothing asked whether anybody was free.
 *
 * So every customer saw all twenty-seven slots as open, always. Two people
 * could book 10:00 AM at the same address on the same morning and both be
 * confirmed, and dispatch absorbed the difference.
 *
 * Worse, the app only found out at the end: payment is taken BEFORE the
 * booking is created (see `PaymentScreen`), so a slot that could not be
 * staffed produced a charge and no booking.
 *
 * ---------------------------------------------------------------------------
 * THE SHAPE, AND WHY IT IS INSTANTS RATHER THAN LABELS
 * ---------------------------------------------------------------------------
 * The server answers with the START INSTANTS of the slots that are full, not
 * with labels like "10:00 AM". Labels would tie the two sides to one format,
 * one language and one timezone, and the first re-word of the grid would
 * silently stop matching. Instants compare as numbers and cannot drift.
 *
 * An empty list is the ordinary answer and means everything is open.
 */

/**
 * When the day opens and closes for bookings, in the city's own local time.
 *
 * ---------------------------------------------------------------------------
 * THE GRID USED TO BE THE APP'S OWN OPINION
 * ---------------------------------------------------------------------------
 * `timeSlots` in `data/booking.ts` was generated from three constants - 07:00
 * to 20:00 every thirty minutes - and `ops.dutyHours.*`, which decides when
 * anybody is actually on duty, had never been connected to it. A city opening
 * at 09:00 still offered 07:00, 07:30, 08:00 and 08:30 with nobody to send; a
 * city working until 21:00 stopped offering slots at 20:00 and wasted the last
 * hour of its capacity.
 */
export type BookingWindow = {
  /** Local time, `HH:mm`. */
  start: string;
  /** Local time, `HH:mm`. The last slot may START here. */
  end: string;
  stepMinutes: number;
};

/**
 * Why one slot cannot be booked, when a RULE closed it rather than a
 * shortage of people.
 *
 * Every one of these is in `fullSlots` too, which is what lets the server
 * ship the rule without an app release: a build that ignores this field still
 * draws the tile dead. What the field adds is the sentence - "we close at
 * 21:00 and this takes 2 hr" is a different problem from "everybody is busy",
 * and they want opposite things from the customer.
 */
export type BlockedSlot = {
  /** The start instant, exactly as it appears in `fullSlots`. */
  at: string;
  /**
   * `past_closing` - the visit would still be running after the city shuts.
   * `too_soon` - it starts inside the booking notice.
   */
  reason: 'past_closing' | 'too_soon';
  /** Minutes between this start and closing. Null for `too_soon`. */
  minutesLeft: number | null;
  /** How long the basket runs, back to back. */
  visitMinutes: number;
};

/** What `GET /availability` returns for one day. */
export type DayAvailability = {
  /** The day asked about, echoed back. `YYYY-MM-DD`. */
  date: string;
  /**
   * The bookable window, when the server has an opinion about one.
   *
   * Absent is NOT "closed today". `ops.dutyHours.enabled` ships off, and while
   * it is off nothing puts anyone on duty by the clock - so there is no window
   * to quote and the app keeps its compiled grid. Reading absence as closed
   * would empty the picker for every deployment that has not turned duty hours
   * on, which is most of them.
   */
  window?: BookingWindow;
  /**
   * Ops closed this date for the city - a holiday, a day nobody can work.
   * Every start is in `fullSlots` too; this only lets the sheet say why.
   */
  closed?: boolean;
  /**
   * The subset of `fullSlots` a rule closed, with the reason.
   *
   * Optional on the type and not in the parsing, because a server older than
   * this field is a perfectly good server - the grid just loses the
   * explanation and keeps the tile dead, which is where it came from.
   */
  blocked?: readonly BlockedSlot[];
  /**
   * Start instants of the slots that can take no more work, as ISO strings.
   *
   * Only the FULL ones. Sending every slot with a count would be a bigger
   * payload saying the same thing, and the app has the grid already - what it
   * lacks is which of those to close.
   *
   * Worked out by the server with the rules dispatch assigns by - who holds
   * the trade, who is on duty then, who is already on a job or at lunch, and
   * the bookings still waiting for somebody. `POST /bookings` refuses the same
   * starts (`SLOT_UNAVAILABLE`), so a tile left open is one that will book.
   */
  fullSlots: string[];
};

/**
 * One booking the basket would become: a service, and its hours when it is
 * sold by the hour. Two of them at 11:00 need two professionals at 11:00, and
 * a three-hour job needs three free hours - so capacity is asked about the
 * basket as it stands, not about a single service.
 */
export type AvailabilityItem = { serviceId: string; hours?: number };

/**
 * Which grid labels on `date` the server says are full.
 *
 * Matched on epoch milliseconds, never on string equality - two correct ISO
 * strings for one moment can differ in offset spelling and milliseconds.
 *
 * `slots` is the grid actually on screen. Matching against the compiled
 * 7:00 AM–8:00 PM grid instead left a city open until 9:00 PM with its last
 * two slots never marked full, however booked out they were.
 */
export const fullLabelsFor = (
  date: Date,
  fullSlots: readonly string[],
  slots: readonly string[],
): Set<string> => {
  const fullAt = new Set(
    fullSlots
      .map(iso => new Date(iso).getTime())
      .filter(ms => !Number.isNaN(ms)),
  );
  const labels = new Set<string>();
  for (const time of slots) {
    const at = new Date(slotStartIso(date, time)).getTime();
    if (!Number.isNaN(at) && fullAt.has(at)) {
      labels.add(time);
    }
  }
  return labels;
};

/**
 * The same instant-to-label mapping as `fullLabelsFor`, carrying the reason.
 *
 * Separate rather than folded in: `fullTimes` is a membership test the grid
 * runs for every tile on every render, and a Map of objects is the wrong
 * shape for that. This one is read only for the tile somebody taps.
 */
export const blockedLabelsFor = (
  date: Date,
  blocked: readonly BlockedSlot[] | undefined,
  slots: readonly string[],
): Map<string, BlockedSlot> => {
  const byInstant = new Map<number, BlockedSlot>();
  for (const entry of blocked ?? []) {
    const ms = new Date(entry.at).getTime();
    if (!Number.isNaN(ms)) byInstant.set(ms, entry);
  }
  const labels = new Map<string, BlockedSlot>();
  for (const time of slots) {
    const at = new Date(slotStartIso(date, time)).getTime();
    const entry = Number.isNaN(at) ? undefined : byInstant.get(at);
    if (entry) labels.set(time, entry);
  }
  return labels;
};

/**
 * Ask what is full on one day, for the address the job would go to.
 *
 * `addressId` rather than coordinates: capacity is a question about who can be
 * SENT somewhere, and the server already resolves a serviceable city off that
 * row - the same lookup `POST /bookings` does. Asking with a raw pin would
 * make this endpoint answer for an area the booking might then be refused for.
 *
 * `serviceId` is optional and narrows the answer to professionals who can
 * actually do that trade. Omitted, the answer is the area's whole capacity,
 * which is the right question for a basket spanning several trades.
 */
export const fetchAvailability = (
  input: {
    /** `YYYY-MM-DD` in the customer's own timezone, not UTC. */
    date: string;
    addressId: string;
    serviceId?: string;
    /** The basket, one entry per booking. Wins over `serviceId`. */
    items?: readonly AvailabilityItem[];
    /** A booking being moved: its own slot must not count against it. */
    bookingId?: string;
  },
  signal?: AbortSignal,
): Promise<DayAvailability> => {
  const query = new URLSearchParams({
    date: input.date,
    addressId: input.addressId,
  });
  if (input.items && input.items.length > 0) {
    query.set(
      'items',
      input.items
        .map(item =>
          item.hours ? `${item.serviceId}:${item.hours}` : item.serviceId,
        )
        .join(','),
    );
  } else if (input.serviceId) {
    query.set('serviceId', input.serviceId);
  }
  if (input.bookingId) {
    query.set('bookingId', input.bookingId);
  }
  /* Under `/bookings`, not at the root: it is mounted on the customer
     bookings controller, beside `quote` and `reschedule`, so it inherits that
     controller's auth guards rather than needing its own. */
  return api.get<DayAvailability>(
    `/bookings/availability?${query.toString()}`,
    {
      signal,
    },
  );
};

/**
 * `YYYY-MM-DD` for a local date.
 *
 * Deliberately not `toISOString().slice(0, 10)`: that converts to UTC first,
 * so any evening in India - which is +5:30 - reports the NEXT day, and the
 * picker would ask about tomorrow while showing today.
 */
export const dateKey = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * One day's question: which day, which address, and what is being booked.
 */
export type AvailabilityQuery = {
  /** `YYYY-MM-DD` in the customer's own timezone. */
  date: string;
  addressId: string;
  serviceId?: string;
  items?: readonly AvailabilityItem[];
  bookingId?: string;
};

/*
 * ---------------------------------------------------------------------------
 * ANSWERS ARE KEPT FOR A MINUTE, AND ASKED FOR BEFORE THEY ARE NEEDED
 * ---------------------------------------------------------------------------
 * Counting who is free is real work on the server, and the picker used to ask
 * it from scratch every time the sheet opened and every time somebody tapped a
 * day - so the grid sat there unanswered for as long as that took, every time.
 *
 * Kept here rather than in the hook, so the cart can ask for the next few days
 * while the customer is still reading their basket, and the sheet opens on an
 * answer that is already in hand. A kept answer is shown at once and then
 * asked for again, so a slot somebody else took in the meantime still closes.
 */
const FRESH_MS = 60_000;

type Entry = {
  at: number;
  day?: DayAvailability;
  pending?: Promise<DayAvailability>;
};

const kept = new Map<string, Entry>();

const keyOf = (query: AvailabilityQuery): string =>
  JSON.stringify([
    query.date,
    query.addressId,
    query.serviceId ?? null,
    query.items ?? null,
    query.bookingId ?? null,
  ]);

/** A kept answer for this exact question, if it is still fresh. */
export const peekAvailability = (
  query: AvailabilityQuery,
): DayAvailability | undefined => {
  const entry = kept.get(keyOf(query));
  return entry?.day && Date.now() - entry.at < FRESH_MS ? entry.day : undefined;
};

/**
 * The answer, asked for once however many callers want it at the same time.
 *
 * `fresh` skips a kept answer - pull-to-refresh, and the re-check after a
 * kept answer has been shown - but still joins a request already in flight.
 */
export const loadAvailability = (
  query: AvailabilityQuery,
  options: { fresh?: boolean } = {},
): Promise<DayAvailability> => {
  const key = keyOf(query);
  const entry = kept.get(key);
  if (entry?.pending) {
    return entry.pending;
  }
  if (!options.fresh && entry?.day && Date.now() - entry.at < FRESH_MS) {
    return Promise.resolve(entry.day);
  }

  const pending = fetchAvailability(query)
    .then(day => {
      kept.set(key, { at: Date.now(), day });
      return day;
    })
    .catch(error => {
      /* A failure is not kept: the next ask tries again. */
      const current = kept.get(key);
      if (current?.pending === pending) {
        if (current.day) {
          kept.set(key, { at: current.at, day: current.day });
        } else {
          kept.delete(key);
        }
      }
      throw error;
    });

  kept.set(key, { at: entry?.at ?? 0, day: entry?.day, pending });
  return pending;
};

/**
 * Ask ahead, one day at a time, and ignore the outcome - a failed prefetch
 * costs nothing; the picker asks again when the day is actually shown.
 */
export const prefetchAvailability = async (
  queries: readonly AvailabilityQuery[],
): Promise<void> => {
  for (const query of queries) {
    try {
      await loadAvailability(query);
    } catch {
      return;
    }
  }
};

/** For tests: forget everything kept. */
export const clearAvailabilityCache = (): void => {
  kept.clear();
};
